// @ts-nocheck
import type { ItemDefinition } from '../../../../../shims/appStub';
import type { CompiledRuntimeNode } from '../compiler';
import type { DataRef } from '../../types/binding';
import type { RenderDescriptor } from '../renderer/types';

function isHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9a-fA-F]{3,8}$/.test(value);
}

function isItemFieldBackgroundColorRef(ref: DataRef | undefined): boolean {
  return ref?.kind === 'itemField' && ref.field === 'backgroundColor';
}

/** Nodes whose style.backgroundColor is bound to item.backgroundColor (scheme-driven). */
export function collectSchemeDrivenBackgroundNodeIds(
  nodes: readonly CompiledRuntimeNode[],
): Set<string> {
  const ids = new Set<string>();
  for (const node of nodes) {
    const bgSource = node.style?.backgroundColor;
    if (bgSource?.kind === 'binding' && isItemFieldBackgroundColorRef(bgSource.ref)) {
      ids.add(node.id);
    }
  }
  return ids;
}

function syncDescriptorTree(
  descriptor: RenderDescriptor,
  pipelineItem: ItemDefinition,
  schemeDrivenNodeIds: ReadonlySet<string>,
): RenderDescriptor {
  let next = descriptor;
  const itemBg = pipelineItem.backgroundColor;
  const itemBorder = pipelineItem.borderColor;

  if (schemeDrivenNodeIds.has(descriptor.nodeId)) {
    const currentFill = descriptor.chrome?.backgroundColor;
    if (isHexColor(itemBg) && isHexColor(currentFill) && currentFill !== itemBg) {
      next = {
        ...next,
        chrome: { ...next.chrome, backgroundColor: itemBg },
      };
    }

    const currentBorder = descriptor.chrome?.borderColor;
    if (isHexColor(itemBorder) && isHexColor(currentBorder) && currentBorder !== itemBorder) {
      next = {
        ...next,
        chrome: { ...next.chrome, borderColor: itemBorder },
      };
    }
  }

  if (!descriptor.children?.length) {
    return next;
  }

  const syncedChildren = descriptor.children.map((child) =>
    syncDescriptorTree(child, pipelineItem, schemeDrivenNodeIds),
  );
  const childrenChanged = syncedChildren.some((child, index) => child !== descriptor.children![index]);
  if (!childrenChanged) {
    return next;
  }

  return {
    ...next,
    children: syncedChildren,
  };
}

/**
 * Builtin fallback paints item.backgroundColor directly. Custom layouts only mirror
 * scheme script writes onto descriptor surfaces explicitly bound to item.backgroundColor.
 */
export function applySchemeItemColorToLayoutDescriptors(
  descriptors: readonly RenderDescriptor[],
  pipelineItem: ItemDefinition,
  schemeDrivenNodeIds: ReadonlySet<string>,
): RenderDescriptor[] {
  if (schemeDrivenNodeIds.size === 0) {
    return [...descriptors];
  }
  if (!isHexColor(pipelineItem.backgroundColor) && !isHexColor(pipelineItem.borderColor)) {
    return [...descriptors];
  }
  return descriptors.map((descriptor) =>
    syncDescriptorTree(descriptor, pipelineItem, schemeDrivenNodeIds),
  );
}
